import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Box,
  Avatar,
  Typography,
  Tooltip,
  IconButton,
  Divider,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { Person } from '../../../types/tree';
import { transformDate } from '../../../utils/date';
import { stringAvatar } from '../../../utils/transformTree';
import { capitalise } from '../../../utils';

type PublicEditFamilyProps = {
  defaultValues: Person;
  closeDrawer: () => void;
  treeMembers?: ReadonlyArray<any>;
  setRootId?: React.Dispatch<React.SetStateAction<string | null>>;
  firstRootId: string;
  resetRootHandler: () => void;
  rootId: string;
  openCreateTree?: () => void;
};

export default function PublicEditFamilyMember({
  defaultValues,
  closeDrawer,
  setRootId,
  firstRootId,
  resetRootHandler,
  rootId,
  openCreateTree,
}: PublicEditFamilyProps) {
  const navigate = useNavigate();
  const [updatedData] = useState<any>(defaultValues);

  const MemberName =
    `${updatedData.first_name} ${updatedData.last_name}` as const;

  return (
    <Box>
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" gutterBottom fontWeight="600">
            {MemberName}
          </Typography>
          <Tooltip title="Close">
            <IconButton onClick={closeDrawer}>
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          mb={2}
        >
          <Avatar
            {...stringAvatar(updatedData)}
            src={updatedData?.photo_url}
            alt="profile picture"
          ></Avatar>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-evenly',
            py: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="Add family members">
                  <IconButton onClick={openCreateTree} color="primary">
                    <GroupAddIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption">Add family member</Typography>
            </Box>
          </Box>
        </Box>
        <Divider />
        {rootId !== firstRootId || rootId !== defaultValues?.id ? (
          <Box
            sx={{
              mt: 1.5,
              p: 1,
              display: 'flex',
              justifyContent: 'space-around',
              borderRadius: 2,
              bgcolor: (theme) => theme.palette.action.hover,
            }}
          >
            {rootId !== defaultValues?.id && defaultValues?.id ? (
              <Tooltip title="Make this person the top of the tree" arrow>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.2,
                  }}
                >
                  <IconButton
                    color="primary"
                    aria-label="Show tree from here"
                    onClick={() => {
                      if (setRootId) setRootId(defaultValues.id);
                      closeDrawer();
                    }}
                  >
                    <AccountTreeIcon />
                  </IconButton>
                  <Typography variant="caption">Family view</Typography>
                </Box>
              </Tooltip>
            ) : null}
            {rootId !== firstRootId ? (
              <Tooltip title="Go back to the original root person" arrow>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.2,
                  }}
                >
                  <IconButton
                    color="warning"
                    aria-label="Reset to root user"
                    onClick={() => {
                      if (resetRootHandler) resetRootHandler();
                      closeDrawer();
                    }}
                  >
                    <RestartAltIcon />
                  </IconButton>
                  <Typography variant="caption">Reset to root</Typography>
                </Box>
              </Tooltip>
            ) : null}
          </Box>
        ) : null}
        <Button
          fullWidth
          sx={{ mt: 1.5 }}
          variant="contained"
          size="small"
          color="info"
          disableElevation
          onClick={() => navigate('/app')}
        >
          Sign up for more
        </Button>

        <Box sx={{ overflowY: 'scroll', maxHeight: 650 }}>
          <Typography variant="h5" py={1} fontWeight="500">
            Information
          </Typography>
          <Divider />
          <Box py={1}>
            <Typography
              variant="subtitle1"
              sx={{ color: (theme) => theme.palette.text.secondary }}
            >
              Name
            </Typography>
            <Typography>{MemberName}</Typography>
          </Box>
          {updatedData?.middle_name && (
            <Box py={1}>
              <Typography
                variant="subtitle1"
                sx={{ color: (theme) => theme.palette.text.secondary }}
              >
                Middle name
              </Typography>
              <Typography>{updatedData?.middle_name}</Typography>
            </Box>
          )}
          {updatedData?.last_name_at_birth && (
            <Box display={'flex'} gap={2} alignItems={'center'}>
              <Typography
                variant="subtitle1"
                sx={{ color: (theme) => theme.palette.text.secondary }}
              >
                Last Name at Birth
              </Typography>
              <Typography>{updatedData?.last_name_at_birth}</Typography>
            </Box>
          )}
          {updatedData?.gender && (
            <Box py={1}>
              <Typography
                variant="subtitle1"
                sx={{ color: (theme) => theme.palette.text.secondary }}
              >
                Gender
              </Typography>
              <Typography>{capitalise(updatedData?.gender)}</Typography>
            </Box>
          )}
          <Divider />
          {updatedData?.date_of_birth && (
            <Box py={1}>
              <Typography
                variant="subtitle1"
                sx={{ color: (theme) => theme.palette.text.secondary }}
              >
                Born
              </Typography>
              <Typography>
                {transformDate(updatedData?.date_of_birth)}
              </Typography>
            </Box>
          )}
          {updatedData?.birth_place && (
            <Box py={1}>
              <Typography
                variant="subtitle1"
                sx={{ color: (theme) => theme.palette.text.secondary }}
              >
                Birth place
              </Typography>
              <Typography>{updatedData.birth_place}</Typography>
            </Box>
          )}
          {!updatedData?.is_alive ? (
            <Box py={1}>
              <Typography
                variant="subtitle1"
                sx={{ color: (theme) => theme.palette.text.secondary }}
              >
                Living status
              </Typography>
              <Typography>Deceased</Typography>
              <Typography>Death date</Typography>
              <Typography>{transformDate(updatedData?.death_date)}</Typography>
            </Box>
          ) : (
            <Box py={1}>
              <Typography
                variant="subtitle1"
                sx={{ color: (theme) => theme.palette.text.secondary }}
              >
                Living status
              </Typography>
              <Typography>Living</Typography>
            </Box>
          )}
          <Divider />
          {updatedData?.bio && (
            <Box display={'flex'} flexDirection={'column'} gap={1}>
              <Typography
                variant="subtitle1"
                sx={{ color: (theme) => theme.palette.text.secondary }}
              >
                Biography
              </Typography>
              <Typography variant="body2" lineHeight={1.8}>
                {updatedData?.bio || 'No bio available.'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
